/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { OptionTypeBase } from "../types/common";
import {
  ApplicationEntityType,
  ContentType,
  FileType,
  HHmmss,
  OrderPriority,
  Sex,
  TimeUnit,
  ddMMyyyy,
  MODALITIES,
} from "../constant";
import { ApplicationEntity } from "../types/ae";
import { IPatientResponse } from "../types/patient";
import { IDiagnosticReport, IPractitioner } from "../types/studies";
import { format } from "date-fns";
import { formatValidDate } from "../helpers";
import { Issuer, isValid } from "..";

export const formatBytes = (size: string | number): string => {
  const parsedSize: number =
    typeof size === "string" ? parseInt(size, 10) : size;

  if (parsedSize === 0) return "0";
  const precision = 2;
  const units = ["bytes", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let bytes = parsedSize;
  while (bytes >= 1024) {
    bytes /= 1024;
    unitIndex += 1;
  }
  const unit = units[unitIndex];
  return `${parseFloat(bytes.toFixed(+precision))} ${unit}`;
};

export const formatRemoteAEName = (ae: ApplicationEntity): string => {
  return ae?.type === ApplicationEntityType.CLOUD
    ? `${ae?.title}`
    : `${ae?.title} (${ae?.ip}:${ae?.port})`;
};

export const formatLocalAEName = (ae: ApplicationEntity): string => {
  return ae?.type === ApplicationEntityType.CLOUD
    ? `${ae?.title}`
    : `${ae?.title}:${ae?.port}`;
};

export const getFullName = (patient: IPatientResponse): string => {
  if (!patient) return "";

  if (!patient?.lastName && !patient?.middleName && !patient?.firstName)
    return "";

  return `${patient?.lastName}^${patient?.firstName}^${patient?.middleName}`.replace(
    /\^+$/,
    ""
  );
};

export const getHumanName = (
  practitioner: IPractitioner
): string | undefined => {
  if (!practitioner) return "";
  // @ts-ignore
  if (practitioner?.name?.length > 0) {
    // @ts-ignore
    const n = practitioner?.name[0];
    if (n.name !== "") return n.name;
    return `${n.prefix || ""} ${n.firstName || ""} ${n.middleName || ""} ${
      n.lastName || ""
    } ${n.suffix || ""}`.trim();
  }
};

export const formatDateDicom = (fmt: string, date?: string): string | null => {
  if (!date) return null;

  // Dicom format 20221212
  if (date.length === 8) {
    return format(
      new Date(
        parseInt(date.slice(0, 4), 10),
        parseInt(date.slice(4, 6), 10) - 1,
        parseInt(date.slice(6, 8), 10)
      ),
      fmt
    );
  }

  if (date.length >= 10) {
    // date like "04/22/2016 00:00:00" or 2024-06-12T16:56:14Z
    return formatValidDate(date, fmt);
  }
  // normal date 2012-12-20
  return format(
    new Date(
      parseInt(date.slice(0, 4), 10),
      parseInt(date.slice(5, 7), 10) - 1,
      parseInt(date.slice(8, 10), 10)
    ),
    fmt
  );
};

// Support AX-2935 => date : new Date(), time = '003322'
export const convertDateAndTimeToRequestTz = (
  date: Date,
  time: string
): Date | null => {
  if (!isValid(date)) return null;
  const dateStr = format(date, ddMMyyyy);
  const dateTimeAsDate = new Date(
    parseInt(dateStr.slice(6, 10), 10),
    parseInt(dateStr.slice(3, 5), 10) - 1,
    parseInt(dateStr.slice(0, 2), 10),
    parseInt(time.slice(0, 2), 10),
    parseInt(time.slice(2, 4), 10),
    parseInt(time.slice(4, 6), 10)
  );
  if (isValid(dateTimeAsDate)) return dateTimeAsDate;
  return null;
};

export const getReportContentTypeLabel = (contentType: string) => {
  switch (contentType) {
    case ContentType.APPLICATION_PDF:
      return FileType.PDF;
    case ContentType.TEXT_HTML:
      return ContentType.HTML;

    case ContentType.TEXT_PLAIN:
      return ContentType.TEXT;
    default:
      return "";
  }
};

export const getReportContentByType = (content: string, type: string) => {
  if (!content) return "";
  if (type === ContentType.TEXT_PLAIN) {
    return content.toString().replace(/(?:\r\n|\r|\n)/g, "<br>");
  }
  return content;
};

export const expandHtmlPlainTextCompatibleContents = <
  T extends { htmlPlainTextCompatible?: boolean }
>(
  contents: T[]
): T[] => {
  return contents?.flatMap((content) => {
    if (content.htmlPlainTextCompatible) {
      return [content, { ...content, contentType: ContentType.TEXT_PLAIN }];
    }

    return [content];
  });
};

export const isViewAsTextContent = (content?: {
  htmlPlainTextCompatible?: boolean;
  contentType?: string;
}): boolean => {
  return !!(
    content?.htmlPlainTextCompatible &&
    content?.contentType === ContentType.TEXT_PLAIN
  );
};

// A rich text editor (e.g. Lexical) can leave behind an "empty" node such as
// `<p class="editor-paragraph"><br></p>` once its content is cleared, instead of an empty string.
// This treats that kind of markup as empty so callers can persist null/"" instead of stray markup.
export const isEmptyRichTextContent = (html?: string | null): boolean => {
  if (!html) return true;

  const container = document.createElement("div");
  container.innerHTML = html;

  const text = (container.textContent || container.innerText || "").trim();
  if (text) return false;

  return !container.querySelector(
    "img, table, iframe, video, audio, canvas, svg"
  );
};

export const normalizeRichTextContent = (html?: string | null): string | null =>
  isEmptyRichTextContent(html) ? null : html;

export const getRenderKey = (gender: string) => {
  let result = "";
  switch (gender) {
    case Sex.A:
      result = "sex.ambiguous";
      break;
    case Sex.F:
      result = "sex.female";
      break;
    case Sex.M:
      result = "sex.male";
      break;
    case Sex.N:
      result = "sex.not_applicable";
      break;
    case Sex.O:
      result = "sex.other";
      break;
    case Sex.U:
      result = "sex.unknown";
      break;
    default:
      break;
  }
  return result;
};

export const getDiagnosticReportOptions = (
  selectedReport?: IDiagnosticReport,
  enableViewTextMode = true
): {
  options: OptionTypeBase[];
  isGeneratePdfReport: boolean;
} => {
  let isGeneratePdfReport = false;
  if (selectedReport && selectedReport.contents) {
    const contents = enableViewTextMode
      ? selectedReport.contents
      : selectedReport.contents.filter((x) => !isViewAsTextContent(x));
    const hasPdf =
      contents.filter((x) => x.contentType === ContentType.APPLICATION_PDF)
        .length > 0;
    isGeneratePdfReport = selectedReport.generatePdf && !hasPdf;
    const uniqueContentIds = new Set(contents?.map((x) => x.id));
    const isOnlyText =
      contents?.filter((x) => x.contentType === ContentType.TEXT_PLAIN).length >
        0 && uniqueContentIds.size === 1;
    let ctes = [] as OptionTypeBase[];
    if (isGeneratePdfReport) {
      if (isOnlyText) {
        const item = contents.find(
          (x) => x.contentType === ContentType.TEXT_PLAIN
        );
        const selectedPdfOption = {
          label: FileType.PDF,
          value: {
            ...item,
            contentType: ContentType.APPLICATION_PDF,
            tag: ContentType.PDF_TAG,
          },
        };
        ctes = contents?.map((ct) => {
          return {
            label: getReportContentTypeLabel(ct.contentType),
            value: {
              ...ct,
              tag: getReportContentTypeLabel(ct.contentType).toLowerCase(),
            },
          };
        }) as OptionTypeBase[];
        ctes = [selectedPdfOption, ...ctes];
      } else {
        const item = contents.find(
          (x) => x.contentType === ContentType.TEXT_HTML
        );
        const selectedPdfOption = {
          label: FileType.PDF,
          value: {
            ...item,
            contentType: ContentType.APPLICATION_PDF,
            tag: ContentType.PDF_TAG,
          },
        };
        ctes = contents?.map((ct) => {
          return {
            label: getReportContentTypeLabel(ct.contentType),
            value: {
              ...ct,
              tag: getReportContentTypeLabel(ct.contentType).toLowerCase(),
            },
          };
        }) as OptionTypeBase[];
        ctes = [selectedPdfOption, ...ctes];
      }
    } else {
      ctes = contents?.map((item) => {
        return {
          label: getReportContentTypeLabel(item.contentType),
          value: {
            ...item,
            tag: getReportContentTypeLabel(item.contentType).toLowerCase(),
          },
        };
      }) as OptionTypeBase[];
    }
    return {
      options: ctes || [],
      isGeneratePdfReport,
    };
  }
  return {
    options: [],
    isGeneratePdfReport: false,
  };
};

export const formatCurrentTimeValue = (): string => {
  const currentDate = new Date();
  const timeStr = format(currentDate, HHmmss);
  return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}`;
};

// This function convert highest time unit.
// Return.
export const convertHighestTimeUnit = (
  minutes: number,
  timeUnitOptions: OptionTypeBase[]
) => {
  const minutesOption = timeUnitOptions.find(
    (x) => x.value === TimeUnit.MINUTES
  );

  const hoursOption = timeUnitOptions.find((x) => x.value === TimeUnit.HOURS);

  const daysOption = timeUnitOptions.find((x) => x.value === TimeUnit.DAYS);

  if (minutes === 0) {
    return {
      value: 0,
      option: minutesOption,
    };
  }

  if (daysOption && minutes >= 1440 && minutes % 1440 === 0) {
    return {
      value: minutes / 1440,
      option: daysOption,
    };
  }

  if (hoursOption && minutes >= 60 && minutes % 60 === 0) {
    return {
      value: minutes / 60,
      option: hoursOption,
    };
  }

  return {
    value: minutes,
    option: minutesOption,
  };
};

// this function return description 1440mn => 1 day.
export const convertUnitTImeMinutesToString = (minutes: number) => {
  const days = Math.floor(minutes / 1440); // 1440 minutes in a day 24 * 60.
  minutes %= 1440;
  const hours = Math.floor(minutes / 60); // 60 minutes in an hour.
  minutes %= 60;

  let result = "";
  if (days > 0) result += `${days} ${days === 1 ? "day" : "days"} `;
  if (hours > 0) result += `${hours} ${hours === 1 ? "hour" : "hours"} `;
  if (minutes > 0)
    result += `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  return result.trim();
};

export const convertHighestDayUnit = (
  minutes: number,
  dayUnitOptions: OptionTypeBase[]
) => {
  const defaultDaysOption = dayUnitOptions.find(
    (x) => x.value === TimeUnit.DAYS
  );
  const defaultMonthsOption = dayUnitOptions.find(
    (x) => x.value === TimeUnit.MONTHS
  );

  const defaultYearsOption = dayUnitOptions.find(
    (x) => x.value === TimeUnit.YEARS
  );
  const oneDayInMins = 24 * 60;
  if (minutes === 0)
    return {
      value: "",
      option: defaultDaysOption,
    };
  const years = Math.floor(minutes / (365 * oneDayInMins)); // 365 days in a year
  const months = Math.floor(minutes / (30 * oneDayInMins)); // 30 days in a month

  if (years > 0 && minutes % (365 * oneDayInMins) === 0) {
    return {
      value: years.toString(),
      option: defaultYearsOption,
    };
  }

  if (months > 0 && minutes % (30 * oneDayInMins) === 0) {
    return {
      value: months.toString(),
      option: defaultMonthsOption,
    };
  }

  return {
    value: (minutes / oneDayInMins).toString(),
    option: defaultDaysOption,
  };
};

// convert Issuer[] to OptionTypeBase.
export const convertIssuersToOptionTypeBase = (
  issuers: Issuer[]
): OptionTypeBase[] => {
  return issuers.map((x) => {
    return {
      label: `${x?.namespaceId} - ${x?.type}`,
      value: x?.id,
    };
  });
};

export const getLabelFromOrderPriority = (priority: OrderPriority) => {
  let result = "";
  switch (priority) {
    case OrderPriority.P:
    case OrderPriority.A:
    case OrderPriority.S:
      result = "High";
      break;
    case OrderPriority.T:
      result = "Medium";
      break;
    case OrderPriority.R:
      result = "Low";
      break;
    default:
      break;
  }

  return result;
};

export const getModalityDescription = (modalities) => {
  return MODALITIES.filter((modality) =>
    modalities.some(
      (value) => value.toLowerCase() === modality.value.toLowerCase()
    )
  )
    .map((modality) => modality.label)
    .join(", ");
};
